import { useMemo, type FC } from "react"
import { useTranslation } from "react-i18next"
import fp from "lodash/fp"
import { Stack, Tooltip } from "@mui/material"
import { DateTime } from "luxon"
import Typography from "@mui/material/Typography"
import BatteryAlertOutlinedIcon from "@mui/icons-material/BatteryAlertOutlined"
import BatteryCharging20OutlinedIcon from "@mui/icons-material/BatteryCharging20Outlined"
import BatteryCharging30OutlinedIcon from "@mui/icons-material/BatteryCharging30Outlined"
import BatteryCharging50OutlinedIcon from "@mui/icons-material/BatteryCharging50Outlined"
import BatteryCharging60OutlinedIcon from "@mui/icons-material/BatteryCharging60Outlined"
import BatteryCharging80OutlinedIcon from "@mui/icons-material/BatteryCharging80Outlined"
import BatteryCharging90OutlinedIcon from "@mui/icons-material/BatteryCharging90Outlined"
import BatteryChargingFullOutlinedIcon from "@mui/icons-material/BatteryChargingFullOutlined"
import Battery20OutlinedIcon from "@mui/icons-material/Battery20Outlined"
import Battery30OutlinedIcon from "@mui/icons-material/Battery30Outlined"
import Battery50OutlinedIcon from "@mui/icons-material/Battery50Outlined"
import Battery60OutlinedIcon from "@mui/icons-material/Battery60Outlined"
import Battery80OutlinedIcon from "@mui/icons-material/Battery80Outlined"
import Battery90OutlinedIcon from "@mui/icons-material/Battery90Outlined"
import BatteryFullOutlinedIcon from "@mui/icons-material/BatteryFullOutlined"
import BatteryUnknownOutlinedIcon from "@mui/icons-material/BatteryUnknownOutlined"
import BatteryChargingFullIcon from "@mui/icons-material/BatteryChargingFull"

import { type BatteryStatus as BatteryStatusType } from "types/device.types"
import SkeletonBox from "widgets/common/SkeletonBox"

interface BatteryStatusProps {
  value: number
  status: BatteryStatusType | null | undefined
  timestamp: string
  isLoading: boolean
}

const getBatteryIcon = (value: number, status: BatteryStatusType | null | undefined) => {
  if (fp.isNil(value)) return <BatteryUnknownOutlinedIcon sx={{ color: "neutral.500" }} />
  if (status === "charging") {
    if (value === 100)
      return <BatteryChargingFullOutlinedIcon sx={{ color: "strong.main" }} />
    if (value >= 90)
      return <BatteryCharging90OutlinedIcon sx={{ color: "strong.main" }} />
    if (value >= 80) return <BatteryCharging80OutlinedIcon sx={{ color: "good.main" }} />
    if (value >= 60) return <BatteryCharging60OutlinedIcon sx={{ color: "fair.main" }} />
    if (value >= 50) return <BatteryCharging50OutlinedIcon sx={{ color: "fair.main" }} />
    if (value >= 30) return <BatteryCharging30OutlinedIcon sx={{ color: "weak.main" }} />
    if (value >= 10)
      return <BatteryCharging20OutlinedIcon sx={{ color: "veryWeak.main" }} />
    return <BatteryChargingFullIcon sx={{ color: "veryWeak.dark" }} />
  } else if (status === "discharging" || !status) {
    if (value === 100) return <BatteryFullOutlinedIcon sx={{ color: "strong.main" }} />
    if (value >= 90) return <Battery90OutlinedIcon sx={{ color: "strong.main" }} />
    if (value >= 80) return <Battery80OutlinedIcon sx={{ color: "good.main" }} />
    if (value >= 60) return <Battery60OutlinedIcon sx={{ color: "fair.main" }} />
    if (value >= 50) return <Battery50OutlinedIcon sx={{ color: "fair.main" }} />
    if (value >= 30) return <Battery30OutlinedIcon sx={{ color: "weak.main" }} />
    if (value >= 10) return <Battery20OutlinedIcon sx={{ color: "veryWeak.main" }} />
    return <BatteryAlertOutlinedIcon sx={{ color: "veryWeak.main" }} />
  }
  return <></>
}

const BatteryStatus: FC<BatteryStatusProps> = ({
  value,
  status,
  timestamp,
  isLoading,
}) => {
  const { t } = useTranslation()

  const formattedValue = useMemo(
    () => (fp.isNil(value) ? "-" : `${Math.floor(value)}%`),
    [value],
  )
  const tooltip = useMemo(
    () => (
      <Stack>
        {fp.isNil(value) ? (
          <div>{t("table.NO_DATA")}</div>
        ) : (
          <>
            {status && <div>{t(`device_information.${status.toUpperCase()}`)}</div>}
            {timestamp && (
              <div>{DateTime.fromISO(timestamp).toFormat("yyyy-MM-dd HH:mm")}</div>
            )}
          </>
        )}
      </Stack>
    ),
    [status, timestamp, value, t],
  )

  const content = useMemo(
    () => (
      <Tooltip title={tooltip}>
        <Stack flexDirection="row" alignItems="center">
          {getBatteryIcon(value, status)}
          <Typography component="p">{formattedValue}</Typography>
        </Stack>
      </Tooltip>
    ),
    [formattedValue, value, status, tooltip],
  )

  if (isLoading) return <SkeletonBox height={"20px"} width={"50px"} />
  return <span>{content}</span>
}

export default BatteryStatus
